import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { RootState } from 'store/rootReducer';
import { getPagaendeAktiviteter } from 'store/utforarePagaendeAktiviteterSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';

import {
  DrawerContainer,
  DrawerSection,
  NavigationDrawerHead,
  SpinnerContainer
} from 'components/drawerSections/DrawerSections';

import { HTTP_STATUS } from 'models';
import { AktivitetPagaendeIndex } from 'models/apiModels/AktivitetPagaendeIndex';

import { AktivitetStatusEnum, AktivitetstypEnum, RegistreringsstatustypEnum } from 'enums';

import classNames from 'classnames';
import { formatDate } from 'utilities/dateUtilities';

type Column = keyof Pick<
  AktivitetPagaendeIndex,
  'aktivitetsnamn' | 'aktivitetsnummer' | 'antal_objekt' | 'aktivitetstatusnamn' | 'registreringstid'
>;

type SortableThProps = {
  columnKey: Column;
  text: string;
  sortOrder: { column: string; desc: boolean };
  alignRight?: boolean;
};

const PagaendeAktiviteter: FC = (): ReactElement => {
  const {
    requestStatus,
    data: pagaendeAktiviteter,
    error
  } = useAppSelector((state: RootState) => state.utforarePagaendeAktiviteter);

  const { hasNoAuthorization, selected_organisation } = useAppSelector((state: RootState) => state.user);
  const isLoading = requestStatus === 'pending';
  const dispatch = useAppDispatch();

  const [sortOrder, setSortOrder] = useState<{ column: Column; desc: boolean }>({
    column: 'registreringstid',
    desc: true
  });

  const sortedAktiviteter: AktivitetPagaendeIndex[] | undefined = useMemo(() => {
    const compareFunction = (a: AktivitetPagaendeIndex, b: AktivitetPagaendeIndex) => {
      const aValue = a[sortOrder.column];
      const bValue = b[sortOrder.column];
      if (sortOrder.desc) {
        if (!aValue || !bValue) return (!aValue ? 1 : 0) + (!bValue ? -1 : 0);
        if (aValue > bValue) return -1;
        if (aValue < bValue) return 1;
        return 0;
      } else {
        if (!aValue || !bValue) return (!aValue ? -1 : 0) + (!bValue ? 1 : 0);
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      }
    };

    return pagaendeAktiviteter && [...pagaendeAktiviteter].sort(compareFunction);
  }, [sortOrder, pagaendeAktiviteter]);

  const onChangeSortOrder = (newColumn: Column) => {
    setSortOrder((prev) => ({
      column: newColumn,
      desc: prev.column === newColumn ? !prev.desc : true
    }));
  };

  const SortableTh = ({ columnKey, text, sortOrder, alignRight = false }: SortableThProps) => {
    return (
      <th
        role="button"
        onClick={() => onChangeSortOrder(columnKey)}
        className={classNames('tw-font-normal', alignRight && 'tw-text-right')}
      >
        {text}
        {sortOrder.column === columnKey && (
          <span aria-hidden="true" className={classNames('raa-icon !tw-no-underline', getArrowIcon(sortOrder.desc))} />
        )}
      </th>
    );
  };

  useEffect(() => {
    if (!hasNoAuthorization && selected_organisation) {
      dispatch(getPagaendeAktiviteter(selected_organisation.organisation_nummer));
    }
  }, [dispatch, selected_organisation, hasNoAuthorization]);

  return (
    <DrawerContainer>
      <NavigationDrawerHead disableOpenMap={true} />
      <DrawerSection>
        <h1 className="tw-heading-lg tw-mb-2">Pågående registrering</h1>
      </DrawerSection>

      <SpinnerContainer showSpinner={isLoading}>
        <DrawerSection>
          {error?.status === HTTP_STATUS.NOT_FOUND || (sortedAktiviteter && sortedAktiviteter.length < 1) ? (
            <p>Inga pågående aktiviteter</p>
          ) : (
            sortedAktiviteter && (
              <div className="tw-w-full tw-max-w-[100dvw] tw-overflow-x-auto">
                <table className="tw-w-full tw-table-auto tw-border-separate tw-border-spacing-2">
                  <thead>
                    <tr className="tw-text-left tw-text-size-0.813 tw-text-raa-orange tw-shadow-[inset_0_-1px_0_0] tw-shadow-raa-gray-3">
                      <SortableTh columnKey="aktivitetsnamn" sortOrder={sortOrder} text="Aktivitet" />
                      <SortableTh columnKey="aktivitetsnummer" sortOrder={sortOrder} text="Aktivitetsnummer" />
                      <SortableTh columnKey="antal_objekt" sortOrder={sortOrder} text="Utkast" />
                      <SortableTh columnKey="aktivitetstatusnamn" sortOrder={sortOrder} text="Status" />
                      <SortableTh columnKey="registreringstid" sortOrder={sortOrder} text="Skapad" alignRight />
                    </tr>
                  </thead>

                  <tbody>
                    {sortedAktiviteter.map((aktivitet) => (
                      <tr key={'pagaende-aktivitet_' + aktivitet.aktivitet_id}>
                        <td className="tw-w-1 tw-max-w-fit tw-whitespace-nowrap">
                          <Link to={getLink(aktivitet)} state={{ action: 'fit' }} className="raa-link">
                            <span
                              aria-hidden="true"
                              className={classNames('raa-icon !tw-no-underline', getListIcon(aktivitet))}
                            />{' '}
                            {aktivitet.aktivitetsnamn ?? 'Namn saknas'}
                          </Link>
                        </td>

                        <td className="tw-whitespace-nowrap">{aktivitet.aktivitetsnummer}</td>
                        <td
                          className={classNames(
                            'tw-whitespace-nowrap',
                            isAktivitetBroken(aktivitet) && 'tw-text-raa-error-red'
                          )}
                        >
                          {aktivitet.antal_objekt}
                        </td>
                        <td className="tw-whitespace-nowrap">{aktivitet.aktivitetstatusnamn}</td>
                        <td className="tw-whitespace-nowrap md:tw-text-right">
                          {formatDate(aktivitet.registreringstid)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </DrawerSection>
      </SpinnerContainer>
    </DrawerContainer>
  );
};

const getLink = (aktivitetPagaendeIndex: AktivitetPagaendeIndex) => {
  const url = `/aktivitet/${aktivitetPagaendeIndex.aktivitet_id}`;
  return aktivitetPagaendeIndex.aktivitetstatus === AktivitetStatusEnum.PLANERAD
    ? `/inventering/${aktivitetPagaendeIndex.aktivitet_id}/preliminar`
    : url;
};

const getListIcon = (aktivitetPagaendeIndex: AktivitetPagaendeIndex) => {
  if (isUnderGranskning(aktivitetPagaendeIndex)) return 'raa-icon-hourglass';
  if (aktivitetPagaendeIndex.aktivitetstyp === AktivitetstypEnum.INVENTERING) return 'raa-icon-collection';
  if (isAktivitetBroken(aktivitetPagaendeIndex)) return 'raa-icon-info';

  return 'raa-icon-edit';
};

const isAktivitetBroken = (aktivitetPagaendeIndex: AktivitetPagaendeIndex) => {
  return aktivitetPagaendeIndex.antal_objekt < 1;
};

const isUnderGranskning = (aktivitetPagaendeIndex: AktivitetPagaendeIndex) => {
  return (
    aktivitetPagaendeIndex.registreringsstatustyp_id === RegistreringsstatustypEnum.INSKICKAD ||
    aktivitetPagaendeIndex.registreringsstatustyp_id === RegistreringsstatustypEnum.AVRAPPORTERAD
  );
};

const getArrowIcon = (desc: boolean) => (desc ? 'raa-icon-arrow-down' : 'raa-icon-arrow-up');

export default PagaendeAktiviteter;
